import i18next from 'i18next';

import { SERVICE_MODES } from './serviceModes';

// Definizione dichiarativa dei campi per modalità di servizio (leg booking).
// I valori vivono FLAT sull'order-leg: riuso dei nomi legacy già in uso sui
// viaggi (`carrozzina`, `allettati`, `accompagnatori`, `fermo_auto`, ...) così
// i leg `transport` restano leggibili da Timeline/cruscotto; nomi inglesi per
// i campi nuovi (`stretcher`, `oxygen_required`, `prm_assistance`, ...).
//
// Field: { key, type: 'number'|'text'|'checkbox'|'select'|'textarea',
//          labelKey, label (fallback IT), required?, min?, step?,
//          options? [{ value, labelKey, label }], size? 'full' }
// I campi del gestore (`booking_config.fields`) restano additivi e vengono
// renderizzati DOPO questi, con lo stesso motore (<DynamicField>).

// Tipo di tratta per modalità: due indirizzi, punto singolo o stazioni.
// NB: il punto singolo porta l'indirizzo su `indirizzo` (non `indirizzo_da`):
// vedi `addressKeyFor`/`getServiceAddress` in `constants/serviceModes`.
export const ROUTE_KINDS = {
  TWO_POINTS: 'two_points',
  SINGLE_POINT: 'single_point',
  STATIONS: 'stations',
};

const MODE_ROUTE = {
  [SERVICE_MODES.TRANSPORT]: ROUTE_KINDS.TWO_POINTS,
  [SERVICE_MODES.TRANSPORT_OBJECTS]: ROUTE_KINDS.TWO_POINTS,
  [SERVICE_MODES.AMBULANCE]: ROUTE_KINDS.TWO_POINTS,
  [SERVICE_MODES.ASSISTANCE]: ROUTE_KINDS.SINGLE_POINT,
  [SERVICE_MODES.TRAIN]: ROUTE_KINDS.STATIONS,
};

export const MODE_FIELDS = {
  // Il caso `transport` riusa letteralmente `TripForm` (_shared/trip), lo
  // stesso form della pagina Trip — vedi `BookingLegFields.jsx` e
  // `_shared/trip/forms/modes/tripLegShape.js`. Le sue chiavi flat (passeggeri,
  // barriere, fermo auto, accompagnatore richiesto, A/R, dimissione, ...) sono
  // elencate in `TRANSPORT_TRIP_KEYS` più sotto: quella lista, non questa
  // (vuota), guida payload/restore per questo service_mode.
  [SERVICE_MODES.TRANSPORT]: [],
  [SERVICE_MODES.TRANSPORT_OBJECTS]: [
    {
      key: 'item_type',
      type: 'select',
      labelKey: 'item_type',
      label: 'Tipo collo',
      icon: 'box',
      options: [
        { value: 'documents', labelKey: 'item_type_documents', label: 'Documenti' },
        { value: 'parcel', labelKey: 'item_type_parcel', label: 'Pacco' },
        {
          value: 'medical_supplies',
          labelKey: 'item_type_medical_supplies',
          label: 'Presidi medici',
        },
        { value: 'luggage', labelKey: 'item_type_luggage', label: 'Bagagli' },
        { value: 'other', labelKey: 'item_type_other', label: 'Altro' },
      ],
    },
    {
      key: 'weight_kg',
      type: 'number',
      labelKey: 'weight_kg',
      label: 'Peso (kg)',
      icon: 'weight-hanging',
      min: 0,
    },
    {
      key: 'dimensions',
      type: 'text',
      labelKey: 'dimensions',
      label: 'Dimensioni (LxPxA cm)',
      icon: 'ruler-combined',
    },
    {
      key: 'fragile',
      type: 'checkbox',
      labelKey: 'fragile',
      label: 'Fragile',
      icon: 'triangle-exclamation',
    },
    {
      key: 'note',
      type: 'textarea',
      labelKey: 'note',
      label: 'Note',
      icon: 'note-sticky',
      size: 'full',
    },
  ],
  [SERVICE_MODES.AMBULANCE]: [
    {
      key: 'stretcher',
      type: 'checkbox',
      labelKey: 'stretcher',
      label: 'Barella',
      icon: 'bed-pulse',
    },
    {
      key: 'wheelchair',
      type: 'checkbox',
      labelKey: 'wheelchair_required',
      label: 'Carrozzina',
      icon: 'wheelchair',
    },
    {
      key: 'oxygen_required',
      type: 'checkbox',
      labelKey: 'oxygen_required',
      label: 'Ossigeno',
      icon: 'lungs',
    },
    {
      key: 'medical_escort',
      type: 'select',
      labelKey: 'medical_escort',
      label: 'Accompagnamento sanitario',
      icon: 'user-nurse',
      options: [
        { value: 'none', labelKey: 'medical_escort_none', label: 'Nessuno' },
        { value: 'nurse', labelKey: 'medical_escort_nurse', label: 'Infermiere' },
        { value: 'doctor', labelKey: 'medical_escort_doctor', label: 'Medico' },
      ],
    },
    {
      key: 'accompagnatori',
      type: 'number',
      labelKey: 'accompagnatori',
      label: 'Accompagnatori',
      icon: 'people-group',
      min: 0,
    },
    {
      key: 'patient_condition',
      type: 'textarea',
      labelKey: 'patient_condition',
      label: 'Condizioni del paziente',
      icon: 'notes-medical',
      size: 'full',
    },
  ],
  [SERVICE_MODES.ASSISTANCE]: [
    {
      key: 'duration_minutes',
      type: 'number',
      labelKey: 'duration_minutes',
      label: 'Durata (minuti)',
      icon: 'clock',
      required: true,
      min: 15,
      step: 15,
    },
    {
      key: 'assistance_type',
      type: 'select',
      labelKey: 'assistance_type',
      label: 'Tipo assistenza',
      icon: 'hands-helping',
      options: [
        { value: 'companionship', labelKey: 'assistance_type_companionship', label: 'Compagnia' },
        { value: 'errands', labelKey: 'assistance_type_errands', label: 'Commissioni' },
        {
          value: 'hospital_assistance',
          labelKey: 'assistance_type_hospital',
          label: 'Assistenza ospedaliera',
        },
        { value: 'other', labelKey: 'assistance_type_other', label: 'Altro' },
      ],
    },
    {
      key: 'note',
      type: 'textarea',
      labelKey: 'note',
      label: 'Note',
      icon: 'note-sticky',
      size: 'full',
    },
  ],
  [SERVICE_MODES.TRAIN]: [
    { key: 'carriage', type: 'text', labelKey: 'carriage', label: 'Carrozza', icon: 'train' },
    { key: 'seat', type: 'text', labelKey: 'seat', label: 'Posto', icon: 'chair' },
    {
      key: 'prm_assistance',
      type: 'checkbox',
      labelKey: 'prm_assistance',
      label: 'Assistenza PRM (Sala Blu)',
      icon: 'wheelchair',
    },
    {
      key: 'accompagnatori',
      type: 'number',
      labelKey: 'accompagnatori',
      label: 'Accompagnatori',
      icon: 'people-group',
      min: 0,
    },
  ],
};

const localizeField = (f) => ({
  ...f,
  label: i18next.t(f.labelKey, { defaultValue: f.label }),
  options: f.options?.map((o) => ({
    value: o.value,
    label: i18next.t(o.labelKey, { defaultValue: o.label }),
  })),
});

/** Campi (label localizzate) della modalità. Calcolo on-demand per la lingua. */
export const getModeFields = (mode) => (MODE_FIELDS[mode] || []).map(localizeField);

/** Tipo di tratta della modalità (fallback: due indirizzi). */
export const getModeRouteKind = (mode) => MODE_ROUTE[mode] || ROUTE_KINDS.TWO_POINTS;

/**
 * La modalità percorre una tratta su strada? Solo in questo caso km e tempo di
 * percorrenza hanno significato: un'assistenza avviene in un punto solo, un
 * viaggio in treno non è guidato da noi. Usato per non sommare distanze
 * inesistenti nei totali e per nascondere il calcolo prezzo a distanza.
 */
export const hasDistance = (mode) => getModeRouteKind(mode) === ROUTE_KINDS.TWO_POINTS;

// Chiavi flat del leg `transport`, riusato via TripForm (vedi commento su
// MODE_FIELDS.transport sopra). Includono i campi passeggeri/barriere/fermo
// auto già noti sui viaggi legacy, più i concetti propri di TripForm senza un
// equivalente booking dedicato (A/R, dimissione, blocco di ritorno): restano
// persistiti flat sul leg per non perdere input utente, anche se non hanno
// oggi una vista read-only o una semantica di prenotazione a sé.
export const TRANSPORT_TRIP_KEYS = [
  'carrozzina',
  'no_carrozzina',
  'allettati',
  'accompagnatori',
  'require_wheelchair',
  'accompagnatore_richiesto',
  'fermo_auto',
  'fermo_auto_incluso',
  'architectural_barriers_from',
  'architectural_barriers_to',
  'note',
  'ar',
  'trip_type',
  'ward',
  'floor',
  'room',
  'checked_resignation',
  'indirizzo_da_ritorno',
  'latlng_da_ritorno',
  'indirizzo_a_ritorno',
  'latlng_a_ritorno',
  'architectural_barriers_from_return',
  'architectural_barriers_to_return',
  'ora_ritorno',
  'return_dates',
];

/** Chiavi di TUTTI i campi per-modalità: usate dai mapper payload/UI. */
export const ALL_MODE_FIELD_KEYS = [
  ...new Set([
    ...Object.values(MODE_FIELDS).flatMap((fields) => fields.map((f) => f.key)),
    ...TRANSPORT_TRIP_KEYS,
  ]),
];

/**
 * Valida i campi required della modalità del leg. Ritorna il messaggio di
 * errore (label del primo campo mancante) oppure null.
 */
export const validateModeFields = (leg) => {
  const missing = getModeFields(leg.service_mode).find(
    (f) => f.required && (leg[f.key] == null || String(leg[f.key]).trim() === '')
  );
  if (!missing) return null;
  return i18next.t('campo_obbligatorio', {
    defaultValue: 'Campo obbligatorio: {{field}}',
    field: missing.label,
  });
};

/**
 * Validazione completa di un leg in forma UI: data, tratta secondo il tipo
 * della modalità e campi required per-modalità. Ritorna messaggio o null.
 * Condivisa tra wizard, aggiunta leg e modifica inline dal dettaglio.
 */
export const validateLeg = (leg) => {
  if (!leg.date) {
    return i18next.t('data_obbligatoria', { defaultValue: 'Data obbligatoria.' });
  }
  const kind = getModeRouteKind(leg.service_mode);
  if (kind === ROUTE_KINDS.STATIONS && (!leg.station_from || !leg.station_to)) {
    return i18next.t('stazioni_obbligatorie', {
      defaultValue: 'Stazioni di partenza/arrivo obbligatorie.',
    });
  }
  // Punto singolo: il luogo vive su `indirizzo` (i leg legacy su `indirizzo_da`).
  if (kind === ROUTE_KINDS.SINGLE_POINT && !leg.indirizzo && !leg.indirizzo_da) {
    return i18next.t('indirizzo_obbligatorio', { defaultValue: 'Indirizzo obbligatorio.' });
  }
  if (kind === ROUTE_KINDS.TWO_POINTS && (!leg.indirizzo_da || !leg.indirizzo_a)) {
    return i18next.t('indirizzi_obbligatori', {
      defaultValue: 'Indirizzi di partenza/arrivo obbligatori.',
    });
  }
  return validateModeFields(leg);
};
